import { useEffect, useState } from 'react';
import { Modal } from '../../../shared/components/Modal';
import type { Categoria, CategoriaInput } from '../types';

interface Props {
  open: boolean;
  categoria: Categoria | null;
  onClose: () => void;
  onSubmit: (input: CategoriaInput) => Promise<void> | void;
  loading?: boolean;
}

const CORES = [
  '#6B21A8',
  '#EC4899',
  '#0EA5E9',
  '#F59E0B',
  '#10B981',
  '#EF4444',
  '#8B5CF6',
  '#64748B',
];

export function CategoriaFormModal({ open, categoria, onClose, onSubmit, loading }: Props) {
  const [nome, setNome] = useState('');
  const [ordem, setOrdem] = useState(0);
  const [cor, setCor] = useState(CORES[0]);
  const [ativo, setAtivo] = useState(true);

  useEffect(() => {
    if (open) {
      setNome(categoria?.nome ?? '');
      setOrdem(categoria?.ordem ?? 0);
      setCor(categoria?.cor ?? CORES[0]);
      setAtivo(categoria?.ativo ?? true);
    }
  }, [open, categoria]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit({ nome: nome.trim(), ordem, cor, ativo });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={categoria ? 'Editar categoria' : 'Nova categoria'}
      size="sm"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
            Cancelar
          </button>
          <button
            type="submit"
            form="categoria-form"
            className="btn-primary"
            disabled={loading || !nome.trim()}
          >
            {loading ? 'Salvando…' : 'Salvar'}
          </button>
        </>
      }
    >
      <form id="categoria-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Nome</label>
          <input
            type="text"
            className="input"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            maxLength={80}
            autoFocus
            required
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Ordem</label>
          <input
            type="number"
            className="input"
            value={ordem}
            onChange={(e) => setOrdem(Number(e.target.value))}
            min={0}
          />
          <p className="mt-1 text-xs text-slate-500">Menor valor aparece primeiro no PDV.</p>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-slate-700">Cor</label>
          <div className="flex flex-wrap gap-2">
            {CORES.map((c) => (
              <button
                key={c}
                type="button"
                className={`h-8 w-8 rounded-full border-2 transition ${
                  cor === c ? 'border-slate-900 scale-110' : 'border-transparent'
                }`}
                style={{ backgroundColor: c }}
                onClick={() => setCor(c)}
                aria-label={`Cor ${c}`}
              />
            ))}
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
            checked={ativo}
            onChange={(e) => setAtivo(e.target.checked)}
          />
          Ativa
        </label>
      </form>
    </Modal>
  );
}
